import { Link } from "react-router-dom";
import { ArrowRight, Award, Building2, Download, FileX, Handshake, Mail, MapPin, PenLine, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useMeta } from "@/lib/useMeta";
import { lines, media } from "@/lib/api";
import { Rich, Reveal } from "@/components/site/Rich";
import { PageHeader } from "@/components/site/Layout";
import { PdfViewer } from "@/components/site/PdfViewer";

const DownloadBtn = ({ m, testid }) => (
  <a href={`${media(m.pdf)}?download=1`} download className="inline-flex items-center justify-center gap-2.5 bg-crimson hover:bg-crimson-hover text-white font-bold text-base rounded-full px-9 py-4 shadow-[0_15px_35px_-10px_rgba(200,16,46,0.6)] transition-[background-color,box-shadow] duration-300 group" data-testid={testid}>
    <Download className="w-5 h-5 group-hover:translate-y-0.5 transition-transform" />{m.button_text}
  </a>
);

export const MediaKit = () => {
  const { content } = useSite();
  useMeta("mediakit");
  const m = content.mediakit;
  if (!m.pdf) {
    return (
      <div className="py-24 flex justify-center font-sans" data-testid="media-kit-page">
        <h1 className="sr-only">{m.heading}</h1>
        <p className="flex items-center gap-2 text-stone-500 text-sm border border-dashed border-stone-300 bg-white rounded-md px-5 py-3" data-testid="media-kit-empty"><FileX className="w-4 h-4" />{m.empty_text}</p>
      </div>
    );
  }
  return (
    <div className="font-sans" data-testid="media-kit-page">
      <h1 className="sr-only">{m.heading}</h1>
      <div className="bg-charcoal">
        <PdfViewer url={media(m.pdf)} />
      </div>
      <a href={`${media(m.pdf)}?download=1`} download className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white font-bold text-sm rounded-full px-5 py-3 shadow-[0_12px_30px_-8px_rgba(200,16,46,0.7)] transition-colors" data-testid="media-kit-floating-download-btn">
        <Download className="w-4 h-4" />{m.button_text}
      </a>
      <section className="bg-charcoal border-t-4 border-crimson" data-testid="media-kit-cta">
        <Reveal className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="flex items-center gap-5">
            <span className="hidden sm:block w-12 h-[2px] bg-brass" aria-hidden />
            <h2 className="font-sans text-2xl sm:text-3xl font-bold text-white" data-testid="media-kit-cta-title">{m.cta_title}</h2>
          </div>
          <DownloadBtn m={m} testid="media-kit-download-btn" />
        </Reveal>
      </section>
    </div>
  );
};

const Initials = ({ name }) => (
  <div className="w-full h-full flex items-center justify-center bg-ivory-alt font-serif text-4xl text-brass">
    {name.split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("")}
  </div>
);

const Block = ({ title, html, icon: Icon, testid }) => (
  <Reveal className="py-12 border-b border-stone-200/70 last:border-0">
    <div data-testid={testid}>
      <div className="flex items-center gap-4 mb-6">
        <span className="w-11 h-11 rounded-md bg-crimson text-white flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
        <h2 className="text-3xl sm:text-4xl font-bold text-crimson uppercase tracking-wide break-words">{title}</h2>
      </div>
      <Rich html={html} className="border-l-[3px] border-brass pl-5 sm:pl-6 text-stone-700 leading-[1.85] text-sm sm:text-[15px] text-justify" />
    </div>
  </Reveal>
);

const AboutCta = ({ a }) => (
  <Reveal className="mt-12">
    <div className="bg-charcoal rounded-xl px-6 sm:px-12 py-12 text-center" data-testid="about-cta">
      <h2 className="text-3xl sm:text-4xl text-white">{a.cta_title}</h2>
      <Rich html={a.cta_html} className="mt-4 text-stone-300 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed" />
      <a href={`mailto:${a.cta_email}`} className="mt-3 inline-block text-brass underline underline-offset-4 text-sm break-all" data-testid="about-cta-email-link">{a.cta_email}</a>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/listing-questionnaire" className="inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white font-bold text-sm rounded-full px-6 py-3 transition-colors" data-testid="about-cta-btn">{a.cta_button}<ArrowRight className="w-4 h-4" /></Link>
        <a href={`mailto:${a.cta_email}`} className="inline-flex items-center gap-2 border border-white/40 text-white font-bold text-sm rounded-full px-6 py-3 hover:bg-white hover:text-charcoal transition-colors" data-testid="about-cta-email-btn"><Mail className="w-4 h-4" />{a.cta_email_button}</a>
      </div>
    </div>
  </Reveal>
);

export const About = () => {
  const { content } = useSite();
  useMeta("about");
  const a = content.about;
  const team = [1, 2, 3, 4].map((n) => ({ n, name: a[`team${n}_name`], role: a[`team${n}_role`], photo: a[`team${n}_photo`] })).filter((t) => t.name?.trim());
  return (
    <div data-testid="about-page">
      <section className="relative bg-charcoal overflow-hidden">
        <img src={media(a.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/70 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
          <span className="block h-[2px] w-12 bg-brass mb-5" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="about-title">{a.hero_title}</h1>
          <p className="mt-4 text-brass font-serif italic text-lg">{a.hero_subtitle}</p>
        </div>
      </section>
      <section className="bg-ivory py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
          <Rich html={a.intro_html} className="font-serif text-xl sm:text-2xl leading-relaxed text-charcoal" testid="about-intro" />
          <div className="flex flex-wrap gap-8 justify-center lg:justify-end">
            {team.map((t) => (
              <div key={t.n} className="w-56 text-center" data-testid={`team-member-${t.n}`}>
                <div className="w-56 h-64 rounded-md overflow-hidden shadow-lg border-4 border-white">
                  {t.photo ? <img src={media(t.photo)} alt={t.name} className="w-full h-full object-cover" /> : <Initials name={t.name} />}
                </div>
                <div className="mt-4 font-serif text-xl text-charcoal">{t.name}</div>
                <div className="text-sm text-stone-500 mt-1">{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-ivory-alt/60 py-16 sm:py-20" data-testid="about-sections">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-brass" data-testid="about-sections-eyebrow">{a.sections_eyebrow}</p>
          <Block title={a.editorial_title} html={a.editorial_html} icon={PenLine} testid="about-editorial" />
          <Block title={a.partners_title} html={a.partners_html} icon={Handshake} testid="about-partners" />
          <Block title={a.experience_title} html={a.experience_html} icon={Award} testid="about-experience" />
          <AboutCta a={a} />
        </div>
      </section>
    </div>
  );
};

const Row = ({ icon: Icon, children, testid }) => (
  <div className="flex gap-4 py-3" data-testid={testid}>
    <Icon className="w-5 h-5 mt-0.5 text-crimson shrink-0" />
    <div className="min-w-0 text-stone-600 break-words leading-relaxed">{children}</div>
  </div>
);

export const Contact = () => {
  const { content } = useSite();
  useMeta("contact");
  const c = content.contact;
  return (
    <div data-testid="contact-page">
      <section className="bg-charcoal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white break-words" data-testid="page-title">{c.heading}</h1>
          <p className="mt-4 text-stone-300 text-base sm:text-lg" data-testid="contact-subtitle">{c.subtitle}</p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <Reveal>
        <div className="bg-white rounded-xl shadow-sm border border-stone-200/70 px-6 sm:px-10 py-8 sm:py-10" data-testid="contact-card">
          <h2 className="text-2xl sm:text-3xl text-charcoal mb-5">{c.card_title}</h2>
          <Row icon={Building2} testid="contact-company"><span className="text-charcoal">{c.company}</span></Row>
          <Row icon={MapPin} testid="contact-address">{lines(c.address_lines).map((l, i) => <div key={i}>{l}</div>)}</Row>
          <Row icon={Mail} testid="contact-email"><a href={`mailto:${c.email}`} className="hover:text-crimson">{c.email}</a></Row>
          <Row icon={Phone} testid="contact-phone"><a href={`tel:${c.phone.replace(/\s/g, "")}`} className="hover:text-crimson">{c.phone}</a></Row>
          <div className="border-t border-stone-200 mt-6 pt-7">
            <p className="text-stone-600" data-testid="contact-note">{c.note}</p>
            <Link to="/listing-questionnaire" className="mt-5 inline-flex items-center gap-2 bg-crimson hover:bg-crimson-hover text-white font-bold text-sm rounded-full px-6 py-3.5 transition-colors group" data-testid="contact-questionnaire-link">
              {c.questionnaire_text}<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
        </Reveal>
      </section>
    </div>
  );
};
